import React, { Children, Component } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CodeIcon, PaletteIcon, CameraIcon, ServerIcon, CpuIcon, ArrowRightIcon, CheckIcon, TargetIcon, LightbulbIcon, TrendingUpIcon, LayersIcon } from 'lucide-react';
import { AnimatedSection, AnimatedChildren, AnimatedChild } from '../components/ui/AnimatedSection';
interface ServiceData {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  subtitle: string;
  challenge: {
    title: string;
    description: string;
  };
  approach: {
    title: string;
    description: string;
  };
  services: string[];
  benefits: {
    icon: ComponentType<{
      className?: string;
    }>;
    title: string;
    description: string;
  }[];
  examples: {
    title: string;
    description: string;
  }[];
}
const servicesData: Record<string, ServiceData> = {
  'digital-engineering': {
    icon: CodeIcon,
    title: 'Digital Engineering',
    subtitle: 'Maßgeschneiderte Softwarelösungen für Ihre Geschäftsprozesse',
    challenge: {
      title: 'Die Herausforderung',
      description: 'Standardsoftware stößt an ihre Grenzen. Ihre Prozesse sind einzigartig, Ihre Anforderungen spezifisch. Sie benötigen digitale Werkzeuge, die exakt zu Ihrem Unternehmen passen.'
    },
    approach: {
      title: 'Unser Ansatz',
      description: 'Wir analysieren Ihre Prozesse, verstehen Ihre Ziele und entwickeln Softwarelösungen, die nahtlos in Ihre Arbeitsabläufe integriert werden.'
    },
    services: ['Individualsoftware & Web-Applikationen', 'Plattformen & Kundenportale', 'API-Entwicklung & Systemintegration', 'Prozessautomatisierung & Workflows', 'Datenbanken & Backend-Systeme', 'Mobile Apps (iOS & Android)', 'Legacy-System-Modernisierung'],
    benefits: [{
      icon: TargetIcon,
      title: 'Passgenaue Lösungen',
      description: 'Software, die exakt auf Ihre Anforderungen zugeschnitten ist.'
    }, {
      icon: TrendingUpIcon,
      title: 'Skalierbarkeit',
      description: 'Architekturen, die mit Ihrem Unternehmen wachsen.'
    }, {
      icon: LayersIcon,
      title: 'Integration',
      description: 'Nahtlose Anbindung an bestehende Systeme.'
    }, {
      icon: LightbulbIcon,
      title: 'Innovation',
      description: 'Moderne Technologien für zukunftssichere Lösungen.'
    }],
    examples: [{
      title: 'Kundenportal für Industrieunternehmen',
      description: 'Self-Service-Plattform für Bestellungen und Support.'
    }, {
      title: 'Automatisierte Auftragsverarbeitung',
      description: 'Workflow-System zur automatischen Verarbeitung.'
    }, {
      title: 'Datenintegrations-Plattform',
      description: 'Zentrale Schnittstelle zur Synchronisation.'
    }]
  },
  'corporate-design': {
    icon: PaletteIcon,
    title: 'Corporate Design & Marke',
    subtitle: 'Visuelle Identitäten, die Vertrauen schaffen',
    challenge: {
      title: 'Die Herausforderung',
      description: 'In einem wettbewerbsintensiven Markt ist eine starke Markenidentität entscheidend.'
    },
    approach: {
      title: 'Unser Ansatz',
      description: 'Wir entwickeln Markenidentitäten, die Ihre Unternehmenswerte visuell erlebbar machen.'
    },
    services: ['Markenstrategie & Positionierung', 'Logo-Design & Corporate Identity', 'Designsysteme & UI-Kits', 'Brand Guidelines & Styleguides', 'Kommunikationsdesign', 'Geschäftsausstattung', 'Digital Brand Assets'],
    benefits: [{
      icon: TargetIcon,
      title: 'Differenzierung',
      description: 'Eine unverwechselbare Identität.'
    }, {
      icon: TrendingUpIcon,
      title: 'Vertrauen',
      description: 'Professionelles Auftreten.'
    }, {
      icon: LayersIcon,
      title: 'Konsistenz',
      description: 'Einheitliche Kommunikation.'
    }, {
      icon: LightbulbIcon,
      title: 'Wiedererkennbarkeit',
      description: 'Prägnante visuelle Elemente.'
    }],
    examples: [{
      title: 'Rebranding für Technologieunternehmen',
      description: 'Komplette Neugestaltung der Markenidentität.'
    }, {
      title: 'Designsystem für Softwareplattform',
      description: 'Umfassendes UI-Kit mit Komponenten.'
    }, {
      title: 'Brand Guidelines für Mittelständler',
      description: 'Detailliertes Handbuch zur Markenanwendung.'
    }]
  },
  'foto-video': {
    icon: CameraIcon,
    title: 'Foto & Videoproduktion',
    subtitle: 'Professionelle visuelle Inhalte für Ihre Kommunikation',
    challenge: {
      title: 'Die Herausforderung',
      description: 'Visuelle Inhalte sind das Herzstück moderner Kommunikation.'
    },
    approach: {
      title: 'Unser Ansatz',
      description: 'Wir produzieren visuelle Inhalte, die Ihre Botschaft transportieren.'
    },
    services: ['Imagefilme & Unternehmensvideos', 'Produktfotografie', 'Eventdokumentation', 'Social Media Content', 'Drohnenaufnahmen', 'Animationen & Motion Graphics', 'Postproduktion & Editing'],
    benefits: [{
      icon: TargetIcon,
      title: 'Emotionale Wirkung',
      description: 'Visuelle Geschichten, die berühren.'
    }, {
      icon: TrendingUpIcon,
      title: 'Professionelle Qualität',
      description: 'Hochwertige Produktion.'
    }, {
      icon: LayersIcon,
      title: 'Vielseitige Nutzung',
      description: 'Content für verschiedene Kanäle.'
    }, {
      icon: LightbulbIcon,
      title: 'Kreative Konzepte',
      description: 'Ideen, die Ihre Marke einzigartig machen.'
    }],
    examples: [{
      title: 'Imagefilm für Industrieunternehmen',
      description: 'Emotionaler Unternehmensfilm.'
    }, {
      title: 'Produktfotografie für E-Commerce',
      description: 'Hochwertige Produktbilder.'
    }, {
      title: 'Social Media Kampagne',
      description: 'Video-Content-Serie.'
    }]
  },
  'technische-loesungen': {
    icon: ServerIcon,
    title: 'Technische Lösungen',
    subtitle: 'Robuste Infrastruktur für Ihre digitalen Systeme',
    challenge: {
      title: 'Die Herausforderung',
      description: 'Digitale Systeme müssen zuverlässig, sicher und performant sein.'
    },
    approach: {
      title: 'Unser Ansatz',
      description: 'Wir analysieren Ihre Infrastruktur und implementieren Lösungen für Stabilität und Performance.'
    },
    services: ['Systemintegration & Middleware', 'Cloud-Migration & -Architektur', 'Performance-Optimierung', 'DevOps & CI/CD-Pipelines', 'Monitoring & Alerting', 'Sicherheitsaudits', 'Wartung & Support'],
    benefits: [{
      icon: TargetIcon,
      title: 'Zuverlässigkeit',
      description: 'Stabile Systeme mit hoher Verfügbarkeit.'
    }, {
      icon: TrendingUpIcon,
      title: 'Performance',
      description: 'Schnelle Ladezeiten.'
    }, {
      icon: LayersIcon,
      title: 'Skalierbarkeit',
      description: 'Infrastruktur, die wächst.'
    }, {
      icon: LightbulbIcon,
      title: 'Sicherheit',
      description: 'Schutz nach aktuellen Standards.'
    }],
    examples: [{
      title: 'Cloud-Migration für Finanzdienstleister',
      description: 'Migration in sichere Cloud-Umgebung.'
    }, {
      title: 'Performance-Optimierung E-Commerce',
      description: 'Reduzierung der Ladezeiten um 60%.'
    }, {
      title: 'DevOps-Transformation',
      description: 'Einführung von CI/CD-Pipelines.'
    }]
  },
  'ki-automation': {
    icon: CpuIcon,
    title: 'KI & Automation',
    subtitle: 'Intelligente Automatisierung für effizientere Prozesse',
    challenge: {
      title: 'Die Herausforderung',
      description: 'Repetitive Aufgaben binden wertvolle Ressourcen.'
    },
    approach: {
      title: 'Unser Ansatz',
      description: 'Wir identifizieren Automatisierungspotenziale und implementieren intelligente Lösungen.'
    },
    services: ['Prozessautomatisierung (RPA)', 'KI-Integration in bestehende Systeme', 'Chatbots & virtuelle Assistenten', 'Datenanalyse & Business Intelligence', 'Predictive Analytics', 'Dokumentenverarbeitung', 'Workflow-Optimierung'],
    benefits: [{
      icon: TargetIcon,
      title: 'Effizienz',
      description: 'Automatisierte Prozesse rund um die Uhr.'
    }, {
      icon: TrendingUpIcon,
      title: 'Kosteneinsparung',
      description: 'Reduzierung manueller Aufwände.'
    }, {
      icon: LayersIcon,
      title: 'Skalierbarkeit',
      description: 'Automatisierung, die mit dem Volumen wächst.'
    }, {
      icon: LightbulbIcon,
      title: 'Datennutzung',
      description: 'Erkenntnisse für bessere Entscheidungen.'
    }],
    examples: [{
      title: 'Automatisierte Rechnungsverarbeitung',
      description: 'KI-gestützte Erfassung und Verarbeitung.'
    }, {
      title: 'Kundenservice-Chatbot',
      description: 'Intelligenter Assistent für Kundenanfragen.'
    }, {
      title: 'Predictive Maintenance',
      description: 'Vorhersage von Wartungsbedarf.'
    }]
  }
};
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  if (!slug || !servicesData[slug]) return <Navigate to="/leistungen" replace data-id="element-536" />;
  const service = servicesData[slug];
  return <div className="w-full" data-id="element-537">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-538">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-539">
          <AnimatedSection data-id="element-540">
            <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6" data-id="element-541">
              <service.icon className="w-7 h-7 text-[#E8781A]" data-id="element-542" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest gradient-text max-w-3xl" data-id="element-543">
              {service.title}
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.1} data-id="element-544">
            <p className="mt-6 text-lg md:text-xl text-white/50 max-w-2xl" data-id="element-545">
              {service.subtitle}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Challenge & Approach */}
      <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-546">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-547">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-548">
            <AnimatedSection data-id="element-549">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white" data-id="element-550">
                {service.challenge.title}
              </h2>
              <p className="mt-4 text-lg text-white/50 leading-relaxed" data-id="element-551">
                {service.challenge.description}
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.1} data-id="element-552">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white" data-id="element-553">
                {service.approach.title}
              </h2>
              <p className="mt-4 text-lg text-white/50 leading-relaxed" data-id="element-554">
                {service.approach.description}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-555">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-556">
          <AnimatedSection data-id="element-557">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tightest gradient-text" data-id="element-558">
              Leistungen im Detail
            </h2>
          </AnimatedSection>
          <AnimatedChildren className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4" staggerDelay={0.05} data-id="element-559">
            {service.services.map(item => <AnimatedChild key={item} data-id="element-560">
                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl" data-id="element-561">
                  <CheckIcon className="w-5 h-5 text-[#E8781A] flex-shrink-0" data-id="element-562" />
                  <span className="text-white/60" data-id="element-563">{item}</span>
                </div>
              </AnimatedChild>)}
          </AnimatedChildren>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-564">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-565">
          <AnimatedSection data-id="element-566">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tightest gradient-text" data-id="element-567">
              Ihr Nutzen
            </h2>
          </AnimatedSection>
          <AnimatedChildren className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" staggerDelay={0.1} data-id="element-568">
            {service.benefits.map(benefit => <AnimatedChild key={benefit.title} data-id="element-569">
                <div className="flex gap-6" data-id="element-570">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-571">
                    <benefit.icon className="w-6 h-6 text-[#E8781A]" data-id="element-572" />
                  </div>
                  <div data-id="element-573">
                    <h3 className="text-xl font-semibold text-white" data-id="element-574">
                      {benefit.title}
                    </h3>
                    <p className="mt-2 text-white/50" data-id="element-575">{benefit.description}</p>
                  </div>
                </div>
              </AnimatedChild>)}
          </AnimatedChildren>
        </div>
      </section>

      {/* Examples */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-576">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-577">
          <AnimatedSection data-id="element-578">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tightest gradient-text" data-id="element-579">
              Anwendungsbeispiele
            </h2>
          </AnimatedSection>
          <AnimatedChildren className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.1} data-id="element-580">
            {service.examples.map(example => <AnimatedChild key={example.title} data-id="element-581">
                <div className="p-8 bg-white/5 border border-white/10 rounded-2xl h-full" data-id="element-582">
                  <h3 className="text-lg font-semibold text-white" data-id="element-583">
                    {example.title}
                  </h3>
                  <p className="mt-3 text-white/50" data-id="element-584">{example.description}</p>
                </div>
              </AnimatedChild>)}
          </AnimatedChildren>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#E8781A]/10 via-[#09090B] to-[#a855f7]/10 border-y border-white/5" data-id="element-585">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-586">
          <AnimatedSection data-id="element-587">
            <div className="max-w-3xl mx-auto text-center" data-id="element-588">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest text-white" data-id="element-589">
                Bereit für den nächsten Schritt?
              </h2>
              <p className="mt-6 text-lg text-white/50" data-id="element-590">
                Lassen Sie uns gemeinsam herausfinden, wie wir Ihre
                Herausforderungen lösen können.
              </p>
              <div className="mt-10" data-id="element-591">
                <Link to="/kontakt" className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#09090B] font-medium rounded-lg hover:bg-white/90 transition-colors" data-id="element-592">
                  Projekt besprechen
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-593" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}